/**
 * Shared chart utilities and components
 * Provides consistent styling and components for all dashboard charts
 * Following Recharts best practices for responsive, accessible charts
 */

import React from "react";

/**
 * Standard chart margins for pie/donut charts
 * Optimized for responsive display with legends
 */
export const CHART_MARGINS = {
  top: 5,
  right: 5,
  left: 5,
  bottom: 5,
};

/**
 * Standard chart margins for vertical bar charts with rotated labels
 * Increased bottom margin for rotated X-axis labels
 * Optimized for better space utilization in 2-column layouts
 */
export const BAR_CHART_MARGINS = {
  top: 10,
  right: 15,
  left: 20,
  bottom: -60,
};

/**
 * Standard chart margins for horizontal bar charts
 * Increased left margin for Y-axis labels
 */
export const HORIZONTAL_BAR_MARGINS = {
  top: 10,
  right: 20,
  left: 120,
  bottom: 10,
};

/**
 * Standard chart margins for simple bar charts (no rotated labels)
 * Optimized for better space utilization in 2-column layouts
 */
export const SIMPLE_BAR_MARGINS = {
  top: 10,
  right: 15,
  left: 20,
  bottom: 10,
};

/**
 * Standard tooltip styling component
 * Enhanced with better positioning, formatting, and accessibility
 */
export const ChartTooltip: React.FC<{
  active?: boolean;
  payload?: any[];
  label?: string;
  formatter?: (value: any, name: string) => [string, string];
  labelFormatter?: (label: string) => string;
  separator?: string;
}> = ({ active, payload, label, formatter, labelFormatter, separator = ":" }) => {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <div 
      className="rounded-lg border bg-background/98 backdrop-blur-sm p-3 shadow-xl z-50 min-w-[160px]"
      role="tooltip"
      aria-label="Chart data tooltip"
    >
      {label && (
        <div className="mb-2.5 font-semibold text-sm text-foreground border-b border-border pb-2">
          {labelFormatter ? labelFormatter(label) : label}
        </div>
      )}
      <div className="space-y-2">
        {payload
          .filter((entry: any) => entry.value !== null && entry.value !== undefined)
          .map((entry: any, index: number) => {
            const [formattedValue, formattedName] = formatter
              ? formatter(entry.value, entry.name || entry.dataKey)
              : [entry.value, entry.name || entry.dataKey];
            
            return (
              <div 
                key={index} 
                className="flex items-center justify-between gap-4 min-w-0"
              >
                <div className="flex items-center gap-2 min-w-0 flex-shrink">
                  <div
                    className="h-3 w-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: entry.color || entry.fill || "#94a3b8" }}
                    aria-hidden="true"
                  />
                  <span className="text-sm text-muted-foreground truncate">
                    {formattedName}
                  </span>
                </div>
                <span className="text-sm font-semibold text-foreground whitespace-nowrap ml-2">
                  {formattedValue}
                </span>
              </div>
            );
          })}
      </div>
    </div>
  );
};

/**
 * Standard axis styling
 * Optimized for readability and consistency
 */
export const AXIS_STYLE = {
  tick: { 
    fill: "hsl(var(--muted-foreground))", 
    fontSize: "11px",
    fontFamily: "inherit"
  },
  label: { 
    fill: "hsl(var(--muted-foreground))", 
    fontSize: "11px",
    fontFamily: "inherit",
    fontWeight: 500
  },
};

/**
 * Standard grid styling
 * Subtle grid lines that don't compete with data
 */
export const GRID_STYLE = {
  stroke: "hsl(var(--muted))",
  strokeDasharray: "3 3",
  strokeOpacity: 0.3,
};

/**
 * Animation configuration for smooth chart transitions
 */
export const CHART_ANIMATION = {
  isAnimationActive: true,
  animationBegin: 0,
  animationDuration: 800,
  animationEasing: "ease-out" as const,
};

/**
 * Pie chart label configuration
 * Custom label renderer for better positioning
 * Handles both direct entry objects and Recharts payload format
 */
export const renderPieLabel = (entry: any, threshold: number = 5) => {
  // Handle Recharts payload format
  const percentage = entry.percentage !== undefined 
    ? entry.percentage 
    : (entry.payload?.percentage !== undefined ? entry.payload.percentage : 0);
  
  if (percentage < threshold) return "";
  return `${percentage.toFixed(0)}%`;
};

